import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Select, MenuItem, TextField, Stack, IconButton, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadPentest, startScan, requestEngagement, approveEngagementRequest, rejectEngagementRequest, revokeEngagementRequest } from './pentestSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const JOB_STATUS_COLOR = {
  completed: tokens.severity.low,
  running: tokens.severity.info,
  queued: tokens.text.muted,
  failed: tokens.severity.critical
};

const TOOL_STATUS_COLOR = { ready: tokens.severity.low, restricted: tokens.severity.medium };
const ENGAGEMENT_STATUS_COLOR = {
  PENDING_APPROVAL: tokens.severity.medium, APPROVED: tokens.severity.low,
  REJECTED: tokens.severity.critical, REVOKED: tokens.severity.critical,
};
const AVAILABLE_TOOLS = ['NMAP', 'ZAP', 'NIKTO', 'METASPLOIT'];
const emptyEngagement = { name: '', justification: '', target_scope: '', allowed_tools: ['NMAP'], window_start: '', window_end: '' };

export default function PentestPage() {
  const dispatch = useDispatch();
  const { tools, engagements, allEngagements, jobs, reports } = useSelector((s) => s.pentest);
  const [selectedTool, setSelectedTool] = useState('NMAP');
  const [selectedEngagement, setSelectedEngagement] = useState('');
  const [target, setTarget] = useState('');
  const [error, setError] = useState(null);
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false);
  const [engagementForm, setEngagementForm] = useState(emptyEngagement);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { dispatch(loadPentest()); }, [dispatch]);

  const runAction = async (thunk, arg) => {
    setError(null);
    const result = await dispatch(thunk(arg));
    if (thunk.rejected.match(result)) setError(result.error?.message || 'That action could not be completed.');
  };

  const handleLaunch = async () => {
    if (!target.trim() || !selectedEngagement) return;
    setError(null);
    const result = await dispatch(startScan({ engagementId: selectedEngagement, toolId: selectedTool, target: target.trim() }));
    if (startScan.rejected.match(result)) {
      setError(result.payload || result.error?.message || 'Could not launch the penetration test.');
      return;
    }
    setTarget('');
  };

  const handleCreateEngagement = async () => {
    if (!engagementForm.name.trim() || !engagementForm.justification.trim() || !engagementForm.target_scope.trim() || !engagementForm.window_start || !engagementForm.window_end) {
      return setError('All fields are required to request an engagement.');
    }
    if (engagementForm.name.trim().length < 3) return setError('Engagement name must be at least 3 characters.');
    if (engagementForm.justification.trim().length < 10) return setError('Justification must be at least 10 characters.');
    if (new Date(engagementForm.window_end) <= new Date(engagementForm.window_start)) return setError('End date/time must be after the start date/time.');
    const scopeEntries = engagementForm.target_scope.split(',').map((x) => x.trim()).filter(Boolean);
    const validHost = (v) => /^(https?:\/\/[^\s/@]+(:\d+)?(\/\S*)?|[A-Za-z0-9.-]+|[0-9a-fA-F:.]+(\/\d{1,3})?|\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?)$/.test(v);
    const badScope = scopeEntries.find((v) => !validHost(v));
    if (badScope) return setError(`Target scope entry "${badScope}" is not a hostname, IP/CIDR or http(s) URL. Separate entries with commas.`);
    if (!engagementForm.allowed_tools.length) return setError('Select at least one allowed tool.');
    setError(null);
    const result = await dispatch(requestEngagement({
      ...engagementForm,
      target_scope: engagementForm.target_scope.split(',').map((s) => s.trim()).filter(Boolean),
      window_start: new Date(engagementForm.window_start).toISOString(),
      window_end: new Date(engagementForm.window_end).toISOString(),
    }));
    if (requestEngagement.rejected.match(result)) {
      setError(result.payload || result.error?.message || 'Could not submit this engagement request.');
      return;
    }
    setEngagementDialogOpen(false);
    setEngagementForm(emptyEngagement);
  };

  const handleReject = async () => {
    if (rejectReason.trim().length < 5) return setError('Rejection reason must be at least 5 characters.');
    await runAction(rejectEngagementRequest, { id: rejectTarget.id, reason: rejectReason.trim() });
    setRejectTarget(null);
    setRejectReason('');
  };

  return (
    <Box>
      <PageHeader
        title="Penetration Testing Toolkit"
        subtitle="Internal reconnaissance, automated scanning, and controlled exploit validation — sandboxed lab environment"
        action={<ModuleControlPanel basePath="/pentest" moduleLabel="Penetration Testing" />}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Jobs This Week" value={jobs.length} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Reports Available" value={reports.length} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Tools Ready" value={tools.filter((t) => t.status === 'ready').length} accent={tokens.severity.low} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Available Tools</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {tools.map((t) => (
                <Box key={t.id} sx={{ p: 1.3, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: tokens.text.primary, fontWeight: 600 }}>{t.name}</Typography>
                    <Chip label={t.status} size="small" sx={{ color: TOOL_STATUS_COLOR[t.status], border: `1px solid ${TOOL_STATUS_COLOR[t.status]}`, backgroundColor: 'transparent' }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: tokens.text.muted }}>{t.category}</Typography>
                  <Typography sx={{ fontSize: '0.76rem', color: tokens.text.secondary, mt: 0.3 }}>{t.description}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Launch Scan</Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <Select size="small" displayEmpty value={selectedEngagement} onChange={(e) => setSelectedEngagement(e.target.value)}>
                <MenuItem value="" disabled>Select approved engagement</MenuItem>
                {engagements.map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
              </Select>
              <Select size="small" value={selectedTool} onChange={(e) => setSelectedTool(e.target.value)}>
                {tools.map((t) => <MenuItem key={t.id} value={t.id} disabled={t.status === 'restricted'}>{t.name}</MenuItem>)}
              </Select>
              <TextField size="small" placeholder="Target (e.g. app.client-a.com)" value={target} onChange={(e) => setTarget(e.target.value)} />
              <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={handleLaunch} disabled={!selectedEngagement || !target.trim()}>
                Launch
              </Button>
              <Typography variant="caption" sx={{ color: tokens.text.muted }}>
                Scans run only against pre-authorized assets in the scope registry.
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ overflow: 'hidden', mb: 2.5 }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Scan Jobs</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Job', 'Tool', 'Target', 'Status', 'Findings', 'Started', 'By'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((j) => (
                  <TableRow key={j.id} hover>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: tokens.text.muted }}>{j.id}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary }}>{j.tool}</TableCell>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem', color: tokens.text.secondary }}>{j.target}</TableCell>
                    <TableCell>
                      <Chip label={j.status} size="small" sx={{ color: JOB_STATUS_COLOR[j.status], border: `1px solid ${JOB_STATUS_COLOR[j.status]}`, backgroundColor: 'transparent' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{j.findings}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{formatRelativeTime(j.startedAt)}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{j.requestedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Assessment Reports</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {reports.map((r) => (
                <Box key={r.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.3, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', color: tokens.text.primary }}>{r.title}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.text.muted, fontFamily: '"JetBrains Mono", monospace' }}>{r.id} · {r.client} · {r.date}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.6 }}>
                    {r.findings.critical > 0 && <Chip label={`${r.findings.critical} crit`} size="small" sx={{ color: tokens.severity.critical, backgroundColor: 'transparent', border: `1px solid ${tokens.severity.critical}` }} />}
                    {r.findings.high > 0 && <Chip label={`${r.findings.high} high`} size="small" sx={{ color: tokens.severity.high, backgroundColor: 'transparent', border: `1px solid ${tokens.severity.high}` }} />}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ overflow: 'hidden', mt: 2.5 }}>
            <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Engagements</Typography>
              <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setEngagementDialogOpen(true)}>Request Engagement</Button>
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted, px: 2, pt: 1 }}>An engagement cannot be approved by its own requester.</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Requested By', 'Window', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {allEngagements.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary, maxWidth: 180 }}>{e.name}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.secondary }}>{e.requested_by}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted }}>{new Date(e.window_start).toLocaleDateString()} – {new Date(e.window_end).toLocaleDateString()}</TableCell>
                    <TableCell><Chip label={e.status.replaceAll('_', ' ')} size="small" sx={{ color: ENGAGEMENT_STATUS_COLOR[e.status], border: `1px solid ${ENGAGEMENT_STATUS_COLOR[e.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {e.status === 'PENDING_APPROVAL' && (
                        <>
                          <Tooltip title="Approve"><IconButton size="small" onClick={() => runAction(approveEngagementRequest, e.id)}><CheckRoundedIcon fontSize="small" sx={{ color: tokens.severity.low }} /></IconButton></Tooltip>
                          <Tooltip title="Reject"><IconButton size="small" onClick={() => setRejectTarget(e)}><CloseRoundedIcon fontSize="small" sx={{ color: tokens.severity.critical }} /></IconButton></Tooltip>
                        </>
                      )}
                      {e.status === 'APPROVED' && (
                        <Tooltip title="Revoke authorization"><IconButton size="small" onClick={() => runAction(revokeEngagementRequest, e.id)}><BlockRoundedIcon fontSize="small" sx={{ color: tokens.severity.critical }} /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!allEngagements.length && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>No engagements requested yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={engagementDialogOpen} onClose={() => setEngagementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Pentest Engagement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" size="small" value={engagementForm.name} onChange={(e) => setEngagementForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
            <TextField label="Justification" size="small" multiline minRows={2} value={engagementForm.justification} onChange={(e) => setEngagementForm((f) => ({ ...f, justification: e.target.value }))} />
            <TextField label="Target scope (comma-separated)" size="small" placeholder="app.client-a.com, 10.0.5.0/24" value={engagementForm.target_scope} onChange={(e) => setEngagementForm((f) => ({ ...f, target_scope: e.target.value }))} />
            <TextField select label="Allowed Tools" size="small" SelectProps={{ multiple: true }} value={engagementForm.allowed_tools}
              onChange={(e) => setEngagementForm((f) => ({ ...f, allowed_tools: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}>
              {AVAILABLE_TOOLS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="Window Start" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={engagementForm.window_start} onChange={(e) => setEngagementForm((f) => ({ ...f, window_start: e.target.value }))} sx={{ flex: 1 }} />
              <TextField label="Window End" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={engagementForm.window_end} onChange={(e) => setEngagementForm((f) => ({ ...f, window_end: e.target.value }))} sx={{ flex: 1 }} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEngagementDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateEngagement}>Submit for Approval</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(rejectTarget)} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Engagement</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline minRows={2} sx={{ mt: 1 }} label="Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} helperText="Minimum 5 characters." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
